/** All user-facing strings. Kannada/Hindi are added later as sibling files with the same keys. */
export const en = {
  app_name: 'SIVA Attendance',
  admin_name: 'SIVA Attendance — Admin',
  loading: 'Loading…',
  api_unreachable: 'Cannot reach the server. Check your connection and try again.',
  app_misconfigured:
    'This copy of the app was built without a server address ({url}), so it cannot reach SIVA. Ask your admin for a new download link.',
  api_bad_response:
    'The server sent something the app could not read. Try again, and tell your admin if it keeps happening.',
  offline_generic:
    'You are offline. Check your connection — the app will catch up once you are back.',
  offline_title: 'No Connection',
  health_offline: 'Offline',
  offline_as_of: 'Showing what we last knew, at {time}. Nothing here has been lost.',
  offline_no_data: 'Connect to the internet to see your hours and to punch.',
  try_again: 'Try Again',
  update_available_title: 'Update Available',
  update_available_body: 'Version {version} of the SIVA Attendance app is ready.',
  update_required_title: 'Update Needed To Punch',
  update_required_body:
    'This version of the app is too old to punch. Install version {version} to carry on — your hours are safe.',
  update_download: 'Download Update',
  update_later: 'Later',
  punch_stale: 'Showing what was on screen a moment ago. Reconnecting…',
  error_generic: 'Something went wrong. Please try again.',
  nav_punch: 'Punch',
  // Short, so five tabs read as five even columns on a phone (18 September 2026).
  nav_hours: 'Hours',
  nav_off_days: 'Weekly Off',
  nav_profile: 'Profile',
  nav_today: 'Today',
  nav_attendance: 'Attendance',
  nav_workers: 'Employees',
  nav_weekly_off: 'Weekly Off',
  nav_settings: 'Settings',
  nav_admins: 'Admins',
  coming_soon: 'Coming in a later task.',
  sign_out: 'Sign Out',
  cancel: 'Cancel',
  confirm: 'Confirm',
  type_to_confirm: 'Type {word} to confirm',
  deactivate: 'Deactivate',
  remove: 'Remove',
  close: 'Close',
  copy: 'Copy',
  copied: 'Copied',
  save: 'Save',
  yes: 'Yes',
  no: 'No',

  // passkeys
  passkey_unsupported:
    'This browser does not support passkeys. Use Chrome on Android or Safari on iPhone.',
  passkey_app_blocked:
    'This phone cannot use fingerprint sign-in inside the app ({why}). Update Android System WebView from the Play Store, then reopen the app. If that does not help, show this message to the office.',
  passkey_cancel_hint:
    'No passkey was used. If your passkey is on another device, choose "Use a phone or tablet" in the prompt and scan the QR code.',
  passkey_cancelled: 'The fingerprint / Face ID prompt was cancelled. Try again.',
  passkey_set_up: 'Set up fingerprint / Face ID',
  passkey_setting_up: 'Waiting for Your Phone…',
  passkey_sign_in: 'Sign in with fingerprint / Face ID',
  passkey_signing_in: 'Checking…',
  passkey_registered_since: 'Passkey set up on {date}',
  passkey_not_registered: 'No Passkey Yet',
  worker_passkey_ok: 'Passkey',

  // worker invite
  invite_title: 'Welcome, {name}',
  invite_intro:
    'This phone will be your attendance device. Set up your fingerprint or Face ID once; after that you punch in and out with it.',
  invite_reregister_intro:
    'Set up your fingerprint or Face ID on this phone. Your previous phone will stop working for attendance.',
  invite_done: 'All set, {name}. Your phone is registered.',
  invite_missing: 'This link is not valid.',
  invite_used: 'This link has already been used.',
  invite_expired: 'This link has expired. Ask the admin for a new one.',
  invite_revoked: 'This link was cancelled. Ask the admin for a new one.',
  invite_next_step: 'One more thing, and then you are ready to punch in.',
  invite_missing_help: 'Ask the admin for a new invite link.',
  invite_used_help: 'If this phone is already set up, sign in below.',
  invite_sign_in: 'Go to Sign In',
  a2hs_reopen_title: 'App on Your Home Screen',
  profile_sign_in: 'Sign-In',
  rereg_back: 'Back to Sign In',
  swap_one_at_a_time: 'You have a request open. You can ask for another once this one is settled.',
  invite_location_note:
    'The app will ask for your location: punching works only at the office, so location must be on.',

  // worker login
  login_title: 'Sign In',
  login_intro: 'Use the fingerprint or Face ID you set up on this phone.',
  login_new_phone: 'Lost your phone / new phone?',
  login_no_invite: 'First time here? Ask the admin for your invite link.',

  // re-registration request
  rereg_title: 'Request a New Passkey',
  rereg_intro:
    'If you changed or lost your phone, tell the admin. They will send you a new link to set up this phone.',
  rereg_intro_signed_in:
    'Request a link for a new phone. Your current phone keeps working until the new one is set up.',
  rereg_phone: 'Your Registered Phone Number',
  rereg_reason: 'Reason',
  rereg_reason_new_phone: 'New Phone',
  rereg_reason_lost_phone: 'Lost Phone',
  rereg_reason_other: 'Other',
  rereg_note: 'Anything Else (Optional)',
  rereg_submit: 'Send Request',
  rereg_sent: 'Request sent. The admin will get back to you with a new link.',

  // profile
  profile_title: 'Profile',
  profile_phone: 'Phone',
  profile_type: 'Employment Type',
  profile_join_date: 'Joined',
  profile_code: 'Employee ID',
  profile_passkey: 'Passkey',
  full_time: 'Full-Time',
  part_time: 'Part-Time',
  profile_request_new_passkey: 'Moving to a new phone? Request a new passkey',

  // admin login
  admin_login_title: 'Admin Sign-In',
  auth_tag: 'Attendance',
  auth_admin_console: 'Admin Console',
  auth_steps: 'Sign-In Steps',
  auth_step_passkey: 'Passkey',
  auth_foot: 'SIVA Logistics · Attendance',
  email: 'Email',
  password: 'Password',
  password_show: 'Show characters',
  password_hide: 'Hide characters',
  continue: 'Continue',
  admin_login_step2: 'Now confirm with your passkey.',
  admin_login_other_account: 'Use a Different Account',
  back: 'Back',
  admin_login_remember: 'Keep me signed in on this device',
  admin_login_remember_hint:
    '30 days on this device only; other devices still sign out after 12 hours.',
  admin_login_forgot: 'Forgot password?',
  forgot_title: 'Reset Your Password',
  forgot_intro:
    'Enter your admin email. If it belongs to an admin account, a reset link is on its way; it works once and expires in 30 minutes.',
  forgot_send: 'Send Reset Link',
  forgot_sent:
    'If that email belongs to an admin account, a reset link has been sent. Check your inbox (and spam) within the next 30 minutes.',
  forgot_back: 'Back to Sign-In',
  reset_title: 'Choose a New Password',
  reset_intro:
    'At least 12 characters. After saving, sign in with the new password and confirm with your passkey as usual.',
  reset_new: 'New Password',
  reset_confirm: 'Confirm New Password',
  reset_mismatch: 'The two passwords do not match.',
  reset_save: 'Save New Password',
  reset_done:
    'Password changed. Every other session was signed out. Sign in with the new password and your passkey.',
  reset_invalid:
    'This reset link is invalid or has expired. Request a new one from the sign-in page.',
  reset_go_sign_in: 'Go to Sign-In',
  admin_login_invalid: 'Email or password is incorrect.',
  admin_login_failures_left: '{n} attempts left before a 15 minute lock.',
  admin_login_locked: 'Too many failed attempts. Try again in {minutes} min.',
  admin_login_enrol_required:
    'This admin has no passkey yet. Ask another admin for an enrolment link, or run the bootstrap command on the server.',

  // admin enrolment
  enrol_title: 'Set up Admin Passkey',
  enrol_intro:
    'Hi {name}. Enter your password, then register the fingerprint / Face ID / Windows Hello of this device. Any previous admin passkey will stop working.',
  enrol_done: 'Passkey set up. You are signed in.',

  // workers page
  workers_title: 'Employees',
  workers_add: 'Add Employee',
  workers_show_inactive: 'Show Deactivated',
  worker_name: 'Name',
  worker_phone: 'Phone',
  worker_type: 'Type',
  worker_engagement: 'Employed As',
  worker_engagement_hint: 'Employees are not supported yet.',
  engagement_contractor: 'Contractor',
  engagement_employee: 'Employee (coming soon)',
  worker_designation: 'Designation',
  worker_designation_none: 'Not set',
  designations_title: 'Designations',
  designations_sub: 'What an employee does. The full name is printed on their documents.',
  designations_code: 'Short Code',
  designations_name: 'Full Name',
  designations_add: 'Add Designation',
  designations_retire: 'Retire',
  designations_restore: 'Restore',
  designations_retired: 'Retired',
  fee_title: 'Salary',
  fee_sub:
    'Monthly, with history. A change is a new rate from a date, never an edit to the old one.',
  fee_from: 'From',
  fee_amount: 'Monthly Salary (₹)',
  fee_note: 'Note',
  fee_add: 'Add Rate',
  delete: 'Delete',
  fee_none: 'No salary set. A pay slip cannot be generated without one.',
  fee_tds: 'TDS u/s 194C',
  fee_tds_note:
    'TDS is deducted at this rate on every pay slip. Section 194C has threshold limits below which deduction is not required, and this system does not apply them. Deducting also creates an obligation to deposit the tax and issue Form 16A each quarter, which this system does not do. Confirm both with your CA.',
  worker_join_date: 'Join Date',
  worker_passkey: 'Passkey',
  worker_status: 'Status',
  worker_active: 'Active',
  worker_inactive: 'Deactivated',
  worker_actions: 'Actions',
  worker_actions_for: 'Actions for {name}',
  worker_open: 'Open Details',
  worker_reregister: 'Re-Register Passkey',
  workers_count: '{n} employees · {ft} full-time · {pt} part-time',
  workers_search: 'Filter Employees',
  workers_no_match: 'No employee matches that search.',
  workers_sort_by: 'Sort By',
  workers_none: 'No employees yet. Add the first one.',
  workers_add_sub: 'The invite link appears once you save.',
  actions: 'Actions',
  worker_new_link: 'New Invite Link',
  worker_deactivate: 'Deactivate',
  worker_reactivate: 'Reactivate',
  worker_deactivate_confirm:
    'Deactivate {name}? Their passkey and sessions are revoked immediately and any open invite link stops working.',
  worker_created: 'Employee added. Share this link with them now; it is shown only once.',
  worker_invite_open: 'Link open until {date}',
  worker_invite_none: 'No Open Link',
  worker_pending_rereg: 'Re-Registration Requested',
  worker_phone_taken: 'This phone number is already used by another account.',
  worker_first_name: 'First Name',
  worker_last_name: 'Last Name',
  worker_code: 'Employee Code',
  worker_code_preview: 'Assigned automatically when you save.',
  worker_code_fixed: 'Assigned automatically and cannot be changed.',
  worker_phone_hint: '10 digits, without the country code',
  worker_section_identity: 'Identity',
  worker_section_contact: 'Contact',
  worker_section_employment: 'Employment',
  worker_phone_taken_by: 'This phone number is already used by {name}.',
  worker_edit: 'Edit',
  worker_updated: 'Saved.',

  // invite link card
  copy_link: 'Copy Link',
  share_whatsapp: 'Share via Whatsapp',
  passkey_device: 'Device',
  link_title: 'Invite Link',
  link_reregister_title: 'Re-Registration Link',
  link_enrol_title: 'Admin Enrolment Link',
  link_shown_once:
    'This link is shown only once. Send it to the employee on WhatsApp or let them scan the QR.',
  link_expires: 'Expires {date}. Single use.',
  link_open_invalidates: 'Creating a new link cancels any earlier unused link.',

  // re-registration panel
  rereg_pending_title: 'Pending Passkey Re-Registrations',
  rereg_pending_none: 'No pending requests.',
  rereg_requested_at: 'Requested {date}',
  approve: 'Approve and Create Link',
  reject: 'Reject',

  // admins page
  admins_deactivate_title: 'Deactivate {name}?',
  admins_deactivate_body:
    'Their passkey and sessions are revoked immediately, and any open enrolment link stops working.',
  admins_code_sent:
    'For safety this needs a code. We sent a six-digit code to {email}; it expires in 10 minutes.',
  admins_code_label: 'Confirmation Code',
  admins_code_attempts: '{n} attempt(s) left.',
  admins_passkey_needed:
    'Email is not configured on this server, so confirm with your own passkey instead.',
  admins_confirm_passkey: 'Confirm with Passkey',
  admins_danger: 'Danger Zone',
  admins_danger_help:
    'Deactivating an admin revokes their passkey and signs them out everywhere. At least one active admin must remain.',
  admins_deactivate_pick: 'Admin to Deactivate',
  admins_title: 'Admins',
  admins_add: 'Add Admin',
  admin_new_enrol_link: 'New Enrolment Link',
  admin_created:
    'Admin created. Give them this enrolment link; they will need the password you just set.',
  admin_email_taken: 'An admin with this email already exists.',
  password_hint: 'At least 12 characters.',
  // punch screen
  punch_in: 'Punch In',
  punch_out: 'Punch Out',
  punch_working: 'Confirm with fingerprint / Face ID…',
  punch_distance: '{distance} m from the office · GPS ±{accuracy} m',
  punch_outside: 'You are {distance} m away. Come within {radius} m of the office to punch.',
  punch_locating: 'Getting Your Location…',
  punch_locating_accuracy: 'Locating… (±{accuracy} m)',
  punch_unverified_chip: 'GPS ±{accuracy} m · location could not be verified',
  punch_unverified_note:
    'Your phone could not pin down its location. The punch will be accepted and marked for the admin to review.',
  punch_done_in_unverified: 'Punched in — location could not be verified.',
  punch_done_out_unverified: 'Punched out — location could not be verified.',
  punch_unverified_short: 'Location Unverified',
  punch_location_help_link: 'Location not accurate? Here’s how to check your phone',
  punch_location_help_ios:
    'iPhone: Settings → Privacy & Security → Location Services → on. Then Safari (or this app on the home screen) → While Using the App, with Precise Location on. Keep Wi‑Fi on; it improves accuracy indoors.',
  punch_location_help_android:
    'Android: Settings → Location → on, with Google Location Accuracy (Wi‑Fi scanning) on. Chrome → Site settings → Location → Allow for this site. Step near a window if the signal is weak.',
  punch_location_unavailable:
    'Could not get a GPS fix yet. Move to an open area and wait a moment.',
  punch_location_unsupported:
    'This browser cannot access your location. Use Chrome (Android) or Safari (iPhone).',
  punch_location_denied_title: 'Location Access Is Blocked',
  punch_location_denied_intro:
    'Punching needs your location. Turn it on for this site, then reload:',
  punch_location_denied_chrome:
    'Chrome (Android): tap the lock / tune icon left of the address bar → Permissions → Location → Allow. Also check Android Settings → Location is on.',
  punch_location_denied_safari:
    'Safari (iPhone): Settings → Apps → Safari → Location → Allow. If you opened this from the home screen: Settings → Privacy & Security → Location Services → Safari Websites → While Using. Then reopen the app.',
  punch_office_not_configured: 'The office location is not set up yet. Ask the admin.',
  punch_wait: 'Wait {seconds} s before the next punch.',
  punch_weak_gps: 'GPS accuracy is low (±{accuracy} m). The punch will be flagged for review.',
  punch_offline: 'You are offline. Connect to the internet to punch.',
  punch_today: 'Today',
  // Punching out below the day's target: a reason is asked for, the punch is never refused.
  early_out_title: 'Leaving Early?',
  early_out_body:
    'You have worked {worked} of {target} today. Tell your admin why you are leaving, and you can punch out — this does not stop you leaving.',
  punch_check_again: 'Check Again',
  punch_checking: 'Checking…',
  early_out_pick: 'Reason',
  early_out_personal_emergency: 'Personal Emergency',
  early_out_unwell: 'Unwell',
  early_out_work_completed: 'Work Completed',
  early_out_other: 'Other',
  early_out_detail: 'In Your Own Words',
  early_out_detail_hint: 'e.g. had to collect my daughter from school',
  early_out_detail_help: 'At least ten characters, so your admin knows what happened.',
  early_out_confirm: 'Confirm Punch Out',
  early_out_pending: 'Early Out — Pending Review',
  hours_early_out_you_said: 'You said: “{reason}”',
  hours_early_out_admin_said: 'Your admin said: “{note}”',
  early_out_approved: 'Early Out — Approved',
  early_out_rejected: 'Early Out — Not Approved',
  punch_no_punches: 'No punches yet today.',
  punch_hours: 'Worked {worked} of {target}',
  punch_since: 'Punched in since {time}',
  punch_done_in: 'Punched in at {time}.',
  punch_done_out: 'Punched out at {time}.',
  punch_type_in: 'In',
  punch_type_out: 'Out',
  punch_auto_closed: 'Auto-Closed',
  punch_by_admin: 'By Admin',
  // The phone's own notification when the day's hours are complete. Not a warning and not a nag:
  // it says the hours are done and that punching out is now up to them.
  shift_done_title: 'Your hours are complete',
  shift_done_body:
    'You have completed your {hours} for today. You can punch out whenever you are ready.',
  // admin: notifications & jobs
  nav_system: 'Admin',
  nav_flags: 'Flags',
  admin_flags_sub: 'What the system noticed and nobody has looked at.',
  admin_flags_open: '{n} open. Each one is a punch that looked wrong, with what was seen.',
  admin_flags_review: 'Review Flags',
  notif_title: 'Admin Notifications',
  notif_intro:
    'Get a push notification on this device (and an email) when employees are still punched in at the digest time and when shifts are auto-closed at the cutoff.',
  notif_enable: 'Enable notifications on this device',
  notif_enabled: 'Notifications are on for this device',
  notif_disable: 'Turn Off on This Device',
  notif_devices_none: 'Not on for any device yet.',
  notif_devices_one: 'On for 1 device.',
  notif_devices: 'On for {n} devices.',
  notif_enabled_now: 'Notifications are on for this device.',
  notif_this_device: 'This Device',
  notif_expired_here:
    'Notifications expired on this device (the browser dropped the subscription). Enable them again.',
  notif_col_device: 'Device',
  notif_col_enabled: 'Enabled',
  notif_col_last: 'Last Delivery',
  notif_last_sent: 'Delivered {time}',
  notif_last_failed: 'Failed {time}',
  notif_last_expired: 'Expired {time}',
  notif_disabled_user: 'Turned off {time}',
  notif_never: 'No Delivery Yet',
  notif_blocked:
    'Notifications are blocked for this site in the browser. Allow them in site settings, then reload.',
  notif_unsupported: 'This browser does not support push notifications.',
  notif_push_unconfigured:
    'Push is not configured on the server (VAPID keys missing). Email still works if SMTP is set.',
  notif_email_unconfigured: 'Email is not configured on the server (SMTP settings missing).',
  health_title: 'Health',
  health_api: 'API',
  health_database: 'Database',
  health_backups: 'Backups',
  health_push: 'Push',
  health_email: 'Email',
  health_ok: 'OK',
  health_attention: 'Needs Attention',
  health_down: 'Down',
  health_not_configured: 'Not Configured',
  health_checked: 'Checked {time}',
  // The Admin page: is everything fine, and what is it costing.
  admin_all_good: 'Everything Is Running Normally',
  admin_one_thing: '1 Thing Needs Your Attention',
  admin_n_things: '{n} Things Need Your Attention',
  admin_activity: '{n} punches in the last 24 hours.',
  admin_attention: 'Needs Your Attention',
  admin_technical: 'Technical Details',
  admin_maintenance: 'Maintenance',
  admin_backup_now: 'Back up Now',
  admin_backup_help:
    'A backup is your own copy of everything. One runs automatically every night; this asks for an extra one.',
  admin_backup_requested: 'Backup asked for. It runs within a minute.',
  admin_backup_not_set_up: 'Backups are not set up on this server yet.',
  admin_costs: 'What This Is Costing',
  admin_costs_collapsed:
    'Server, storage and what the account is spending. Opening this asks Amazon for an up-to-date figure, which costs about a rupee.',
  admin_costs_view: 'View Usage',
  admin_costs_refreshing: 'Asking Amazon for the latest figure…',
  wo_partial_hint:
    'Everybody who fits has been placed; the remaining {n} are under Unassigned. Put them on a day (Move Anyway if a cap is in the way), or raise the caps and generate again. The week cannot be published until everybody has a day.',
  admin_costs_graph_alt: 'Spending for each day of this month',
  worker_shift: 'Shift',
  photo_view: 'View {name}’s photo',
  photo_add: 'Add photo',
  photo_change: 'Change photo',
  photo_remove: 'Remove',
  photo_saving: 'Saving…',
  photo_crop_title: 'Frame Your Photo',
  photo_crop_hint:
    'Drag to move. Pinch or use the slider to zoom. Keep the face inside the circle.',
  photo_zoom: 'Zoom',
  photo_use: 'Use This Photo',
  photo_failed: 'The photo could not be saved. Try again.',
  photo_unreadable: 'That file could not be read as a picture.',
  android_seen_on: 'on {n} phone(s)',
  android_newest_in_use: 'Newest Build In Use',
  android_require_newest: 'Everyone must be on the newest build',
  android_rule_would_stop:
    'On the newest build in use, {n} {people} would be sent to the update screen instead of punching: {names}.',
  android_require_newest_help:
    'An older app is sent to the update screen instead of punching. "Newest" is whatever any phone has reported.',
  android_require_newest_on: 'Saved. An older app is now sent to the update screen.',
  android_require_newest_off: 'Saved. Any build can punch again.',
  android_not_installed: 'App not installed',
  android_iphone_no_app: 'iPhone — no app to install',
  android_unknown_phone: 'No app, phone unknown',
  android_not_enrolled: 'No passkey yet',
  android_no_app_android:
    '{n} {people} on Android phones have never punched from the app — it is not installed on their phone: {names}.',
  android_no_app_apple:
    '{n} {people} on iPhones, where there is no app to install. A minimum never applies to them.',
  android_no_app_unknown:
    '{n} {people} have punched from a browser and we cannot tell what phone they carry.',
  passkey_app_old:
    'This app is too old for fingerprint sign-in on this phone. Update it and the sign-in will work.',
  passkey_app_update: 'How to update',
  passkey_interrupted:
    'The sign-in did not finish — the phone closed the app while it was asking for your fingerprint. Tap Sign In again.',
  roster_shift_heading: '{shift} Associates',
  roster_off_that_day: 'Off that day',
  roster_week_off: 'Week off',
  roster_is_off: 'Their off day this week',
  roster_make_off: 'Make {day} their off day',
  roster_change_day: 'Change {day}',
  roster_adjusted: 'Changed by an admin',
  roster_rec_present: 'Present',
  roster_rec_absent: 'Absent',
  roster_rec_off: 'Week off',
  roster_rec_holiday: 'Holiday',
  roster_no_off: 'No off day',
  roster_give_off: 'Give an off day',
  wo_everyone: 'Everyone’s Off Days',
  wo_col_day: 'Day',
  wo_col_off: 'Off That Day',
  wo_you: 'You',
  wo_nobody_off: 'Nobody',
  passkey_home: 'Passkey lives on',
  passkey_home_short: 'Key on {where}',
  login_app_version: 'App {name} (build {code})',
  profile_app_version: 'App Version',
  profile_web_build: 'Screens Updated',
  pk_home_apple: 'iPhone or iPad',
  pk_home_android: 'Android phone',
  pk_home_windows: 'Windows PC',
  pk_home_portable: 'Password manager (any device)',
  pk_home_unknown: 'Not reported',
  shift_first: '1st Shift',
  shift_second: '2nd Shift',
  admin_costs_graph_title: 'Day by Day This Month',
  admin_costs_graph_peak: 'Busiest day {amount}',
  admin_costs_graph_hint: 'Tap a day',
  admin_costs_not_configured:
    'Not Configured. Add read-only billing keys to see what the server costs each month — the runbook has the steps.',
  admin_costs_as_of: 'As of {time}',
  admin_costs_stale: 'Showing the last figures we could fetch. {why}',
  admin_costs_this_month: 'So Far This Month',
  admin_costs_budget: '{used} of your {budget} budget',
  admin_costs_bar_alt: 'Spending Against Budget',
  admin_costs_last_month: 'Last month: {amount}',
  admin_costs_rate_ecb: 'Rupees at ₹{rate} per dollar, the European Central Bank rate for {date}.',
  admin_costs_rate_fixed: 'Rupees at ₹{rate} per dollar, the rate set on the server.',
  admin_costs_rate_none: 'Rupee figures will appear once an exchange rate can be fetched.',
  admin_email_setup: 'Setting up Email',
  admin_email_setup_body:
    'Email is how password resets and the daily and monthly reports reach you. Until it is set up, none of those send.',
  admin_email_step_1:
    'Ask your email provider for SMTP details: a host, a port, a user and a password.',
  admin_email_step_2:
    'Put them in the .env file on the server as SMTP_HOST, SMTP_PORT, SMTP_USER, SMTP_PASS and MAIL_FROM.',
  admin_email_step_3: 'Redeploy. This page stops asking once messages start sending.',
  logs_title: 'Logs',
  logs_errors: 'API Errors',
  logs_notifications: 'Notification Failures',
  logs_backups: 'Backups',
  logs_server: 'Server',
  logs_none_errors: 'No API errors recorded.',
  logs_none_notifications: 'No delivery failures.',
  logs_none_backups: 'No backups recorded yet.',
  logs_disk: 'Disk',
  logs_memory: 'API Memory',
  logs_of: '{used} of {total}',
  logs_no_limit: 'No Limit',
  logs_col_time: 'Time',
  logs_col_request: 'Request',
  logs_col_route: 'Route',
  logs_col_message: 'Message',
  logs_col_channel: 'Channel',
  logs_col_to: 'To',
  logs_col_error: 'Error',
  logs_col_file: 'File',
  logs_col_size: 'Size',
  logs_col_s3: 'S3',
  logs_s3_uploaded: 'Uploaded',
  logs_s3_skipped: 'Local Only',
  logs_s3_failed: 'Failed',
  logs_monthly_copy: 'Monthly Copy',
  jobs_last: 'Last Run',
  jobs_never: 'Never',
  jobs_maintenance: 'Maintenance',
  job_auto_close: 'Auto-Close Open Shifts',
  // Names no time: the cutoff is a setting, and a sentence that says 04:00 is wrong the day it
  // is changed. The System page shows the setting beside it.
  job_auto_close_desc:
    'At the day cutoff every shift still open is closed and the day is computed.',
  job_admin_digest: 'Admin Digest',
  job_admin_digest_desc: 'At 22:00 you get a push and an email listing who is still punched in.',
  job_daily_report: 'Daily Report',
  job_daily_report_desc:
    'At 21:00 every admin gets the day’s attendance report as a PDF, with a short summary.',
  jobs_preview_daily_report: 'This will email the report for {day} to every admin: {summary}',
  jobs_preview_daily_report_none: 'No employees are scheduled for {day}; nothing would be sent.',
  job_data_retention: 'Data Retention',
  job_data_retention_desc:
    'At 03:30 notification rows older than 90 days and punch attempts older than 180 days are deleted. Attendance data is kept forever.',
  jobs_preview_retention:
    'This will delete {n} notification row(s) and {p} punch attempt(s) that are past their retention period.',
  job_monthly_report: 'Monthly Report',
  job_monthly_report_desc:
    'On the 1st, at the daily report time, every admin gets last month’s report as a PDF.',
  jobs_preview_monthly: 'This will email the report for {month} to every admin: {summary}',
  jobs_preview_monthly_off: 'The monthly report is switched off in Settings.',
  job_weekly_off_draft: 'Weekly-Off Draft',
  job_weekly_off_draft_desc:
    'Sunday 00:05: the off-day plan for the week after next is drafted for you to review (two weeks ahead).',
  job_weekly_off_publish: 'Weekly-Off Publish',
  job_weekly_off_publish_desc:
    'Sunday 20:00: next week’s draft is published if you have not done it yourself.',
  jobs_title: 'Scheduled Jobs',
  jobs_intro: 'Runs on the office clock. Missed runs are caught up when the server restarts.',
  jobs_next: 'Next Run',
  jobs_run_now: 'Run Now',
  jobs_dry_run: 'Preview',
  jobs_confirm_title: 'Run {job} now?',
  jobs_confirm_type_run:
    'This uses the current time as the slot and changes real data. Type RUN to confirm.',
  jobs_preview_auto_close: 'This will auto-close {n} open shift(s) now: {names}.',
  jobs_preview_auto_close_none: 'No shift is open right now; nothing would be closed.',
  jobs_preview_digest: 'This will send a digest listing {n} employee(s) still punched in: {names}.',
  jobs_preview_digest_none: 'Nobody is punched in; nothing would be sent.',
  jobs_history: 'Recent Runs',
  jobs_run_details: 'Open the Run Details',
  jobs_run_now_hint: 'Runs the job now against real data',
  logs_retry: 'Retry',
  logs_retry_result: 'Retried: {sent} delivered, {failed} failed.',
  jobs_punched_in_now: 'Currently Punched In',
  jobs_none_punched_in: 'Nobody is punched in right now.',
  jobs_recompute: 'Recompute Summaries (Last 35 Days)',
  jobs_recompute_done: 'Recomputed {days} days for {employees} employees.',
  // settings & holidays
  holidays_title: 'Company Holidays',
  holidays_intro:
    'On a holiday nobody is expected to work: the day is excluded from targets and shows as "holiday". Punches on a holiday still count.',
  holiday_date: 'Date',
  holiday_name: 'Label',
  holiday_name_placeholder: 'E.g. Diwali',
  holiday_add: 'Add Holiday',
  holiday_remove: 'Remove',
  holiday_remove_confirm: 'Remove {name} on {date}?',
  holiday_exists: 'There is already a holiday on that date.',
  holidays_none: 'No holidays in the last year.',
  // weekly off
  wo_title: 'My Weekly Off',
  wo_sub:
    'One day off each week, set by the office. You can ask to move it while the week has not started.',
  wo_off_on: 'Off on {day}',
  wo_off_short: 'Off',
  wo_strip_label: 'The days of the week',
  wo_this_week: 'This Week',
  wo_next_week: 'Next Week',
  wo_week_after_next: 'Week After Next',
  wo_prev_week: 'Previous week',
  wo_next_week_btn: 'Next week',
  wo_pick_week: 'Pick a week',
  wo_past_note: 'This week has already been. Press a day to correct it; the reason is kept.',
  wo_past_week: 'Past Week',
  wo_past_none: 'No weekly-off plan was published for this week.',
  wo_set_off_title: 'Set the week off?',
  wo_set_off_move: '{name} is off on {from}. Make it {to} instead?',
  wo_set_off_new: 'Give {name} their week off on {to}?',
  wo_set_off_do: 'Set Week Off',
  wo_day_title: '{name}',
  wo_choice_off: 'Weekly off',
  wo_choice_present: 'Present',
  wo_choice_absent: 'Absent',
  wo_choice_now: 'Now',
  wo_choice_future: 'Present and absent can be set once the day has come.',
  wo_choice_note: 'Reason',
  wo_choice_note_ph: 'Why this day is being changed',
  wo_mark_present: 'Mark Present',
  wo_mark_absent: 'Mark Absent',
  wo_day_saved: '{name}, {date}: saved.',
  wo_status_none: 'No Plan Yet',
  wo_status_draft: 'Draft (employees cannot see it)',
  wo_status_published: 'Published',
  wo_roster: '{n} employees on the roster',
  wo_pending: '{n} change request(s) pending',
  wo_generate: 'Generate Draft',
  wo_regenerate: 'Regenerate Draft',
  wo_regenerate_confirm: 'Regenerate the draft? Every assignment in this draft will be replaced.',
  wo_generated: 'Draft generated.',
  wo_publish: 'Publish',
  wo_publish_confirm: 'Publish the plan for the week of {week}? Employees will see their off days.',
  wo_published: 'Published. Employees can now see their off days.',
  wo_swap_queue: 'Swap Requests Waiting For You',
  wo_swap_queue_sub:
    'Approving applies the change to that week’s roster; rejecting leaves the off day where it is. Both tell the employee.',
  wo_for_week: 'week of {date}',
  wo_pending_swaps: 'Pending Swaps',
  wo_swap_agreed: 'With {name} — both employees agreed',
  wo_swap_waiting: 'Waiting for {name} to answer',
  wo_swap_no_colleague: 'No colleague named — a move request',
  wo_swap_wait_first: 'Waiting for the colleague to accept.',
  wo_swap_approve: 'Approve',
  wo_swap_reject: 'Reject',
  wo_swap_reject_body: 'Both employees are told, and no off day changes.',
  wo_swap_approved: 'Swap approved. Both off days have moved.',
  wo_swap_rejected: 'Swap rejected. Nothing moved.',
  wo_unassigned: 'Unassigned',
  wo_none: 'Nobody',
  wo_move_to: 'Move {name} to',
  wo_move: 'Move',
  wo_move_hint: 'Move to Another Day',
  wo_moved_by_admin: 'Moved by Admin',
  wo_blocked: 'Cannot move {name} to {day}: a cap would be exceeded.',
  wo_suggest: 'Suggested swap: move {other} to {day} instead.',
  wo_move_anyway: 'Move Anyway',
  wo_apply_swap: 'Apply Swap',
  wo_no_swap: 'No valid swap exists; raise a cap or pick another day.',
  wo_swap_done: 'Swap applied.',
  wo_not_published: 'Not published yet — expected by Sunday evening.',
  wo_no_off_this_week: 'No off day is assigned to you this week.',
  // Worker-to-worker weekly off swaps.
  swap_request: 'Request a Swap',
  swap_my_requests: 'My Swap Requests',
  swap_current_day: 'Your off day that week is {day}.',
  swap_pick_day: 'Move It To',
  swap_pick_day_placeholder: 'Choose a Day',
  swap_pick_colleague: 'Swap With',
  swap_pick_colleague_help:
    'Pick someone already off that day and they are asked first. Leave it blank to ask an admin to move you.',
  swap_no_colleague: 'Nobody in particular — ask an admin',
  swap_nobody_off: 'Nobody is off on that day this week.',
  swap_reason: 'Reason (Optional)',
  swap_reason_hint: 'E.g. a Family Function',
  swap_send: 'Send Request',
  swap_cancel: 'Cancel Request',
  swap_accept: 'Accept',
  swap_decline: 'Decline',
  swap_asked_title: '{name} would like to swap off days',
  swap_asked_body: 'They would take your {theirs} and you would take their {yours}.',
  swap_state_waiting: 'Waiting for {name} to answer.',
  swap_state_asked_you: 'Waiting for your answer.',
  swap_state_with_admin: 'With an admin for approval.',
  swap_state_approved: 'Approved. Your off day has moved.',
  swap_state_rejected: 'Not approved. Your off day is unchanged.',
  swap_state_declined: '{name} could not swap this time.',
  swap_state_you_declined: 'You declined this swap.',
  swap_state_cancelled: 'You withdrew this request.',
  wo_request_later: 'Need a different day? Ask the admin.',
  // today
  today_yesterday: 'Yesterday',
  att_date: 'Date',
  date_open_calendar: 'Open Calendar',
  att_date_group: 'Choose a Day',
  att_prev_day: 'Previous Day',
  att_next_day: 'Next Day',
  att_view_day: 'Day',
  att_view_week: 'Week',
  att_download: 'Download',
  att_download_csv: 'CSV (Spreadsheet)',
  att_download_pdf: 'PDF',
  att_download_help:
    'Report for the selected day: employee, type, status, in, out, hours, target, shortfall, flags.',
  // Admin day corrections. The computed day is never overwritten; a correction sits on top of
  // it and says who changed it and why.
  adj_title: 'Adjust This Day',
  adj_open: 'Adjust Day',
  adj_marker: 'Adjusted',
  adj_status: 'Status',
  adj_status_keep: 'Leave as Computed',
  adj_hours: 'Hours Worked',
  adj_hours_keep: 'Blank: as recorded, or a full day if absent',
  adj_note: 'Why (Required)',
  adj_note_hint: 'e.g. worked from the depot, no phone signal',
  adj_remove: 'Remove Adjustment',
  adj_bad_hours: 'Hours must be between 0 and 24.',
  // Correcting several days with one note. Reached from the Attendance day view (several
  // workers, one date) and from a worker's month grid (one worker, several dates).
  adj_status_choose: 'Choose a Status',
  adj_pick_count: '{n} of {total} selected',
  adj_pick_all: 'Select All',
  adj_pick_none: 'Clear',
  adj_pick_empty: 'Nothing to choose from on this day.',
  adj_many_note_hint: 'The same note is recorded against every day you have selected.',
  adj_save_many: 'Adjust {n} Days',
  // Choosing days on the calendar, or workers in the list, rather than out of a second list of
  // tick-boxes that repeated what was already on screen.
  adj_bar_label: 'Adjust the days you have selected',
  adj_bar_count: '{n} selected',
  adj_bar_apply: 'Adjust {n}',
  adj_bar_applying: 'Adjusting…',
  adj_bar_hint: 'The same status and note are recorded against every employee you selected.',
  adj_select_days: 'Select Days',
  adj_select_workers: 'Select Employees',
  adj_selecting_days: 'Click the days you want to change. Shift-click to take a run of them.',
  adj_selecting_workers:
    'Tap each employee whose day you want to change, then choose a status below.',
  adj_done: 'Done',
  adj_many_workers: 'Adjust Several Employees',
  adj_many_workers_title: 'Adjust Several Employees',
  adj_many_workers_sub: 'Everyone employed on {date}',
  adj_many_days: 'Adjust Several Days',
  adj_many_days_title: 'Adjust Several Days',
  adj_many_days_sub: '{name} · {month}',
  adj_open_for: 'Adjust {name}',
  att_target: 'target',
  att_present: 'Present',
  att_absent: 'Absent',
  att_off: 'Weekly Off',
  att_no_punchout: 'No Punch-Out',
  att_hours_chart: 'Hours that Day',
  att_flags_this_day: 'Only This Day',
  att_from_summaries: 'Past days come from the daily summaries (recomputed nightly at the cutoff).',
  att_week_title: 'Week of {week}',
  att_week_sub: 'Hours per employee and day. Click a cell to open that employee’s day.',
  att_cell_off: 'Weekly Off',
  att_cell_in_progress: 'In Progress',
  early_outs_title: 'Early Punch-Outs',
  early_outs_sub: 'Left before their hours were done, and said why. Approving credits the day.',
  early_outs_of: 'Of',
  early_outs_approve: 'Approve',
  early_outs_reject: 'Reject',
  att_cell_future: 'Still to Come',
  att_cell_no_record: 'Nothing Recorded',
  att_cell_before_join: 'joined on {date}',
  today_show_yesterday: 'Show Yesterday',
  today_day_switch: 'Day',
  today_below_target_line: '{n} of {total} punched out below target so far.',
  today_below_target_none: 'Everyone who punched out reached their target.',
  today_in: 'Punched In',
  today_out: 'Punched Out',
  today_not_yet: 'Not Yet Punched',
  today_off: 'Weekly Off',
  // The Attendance day as one table with a side panel (15 October 2026).
  day_filter: 'Show',
  day_filter_name: 'Filter by name',
  day_tab_all: 'All',
  day_tab_working: 'Working',
  day_tab_not: 'Not Punched',
  day_tab_later: 'Later Shift',
  day_status_working: 'Working',
  day_status_not: 'Not punched',
  day_status_later: 'Shift at {time}',
  day_worked_target: 'Worked / Target',
  day_of_target: 'of {target}',
  day_on_off_day: 'off day',
  day_weak_gps: 'weak GPS',
  day_unverified: 'unverified',
  day_none_here: 'Nobody here.',
  day_right_now: 'Right now',
  day_that_day: 'That day',
  day_in_of_due: '{n} of {due} due came in',
  day_start_later: '{n} start later today',
  day_needs_decision: 'Needs a Decision',
  day_nothing_waiting: 'Nothing is waiting on you.',
  day_early_outs: '{n} early punch-out(s)',
  day_flags_open: '{n} flags open',
  day_see_flags: 'See flags',
  day_no_punchout_yesterday: '{n} no punch-out yesterday',
  day_open: 'Open',
  day_coming_up: 'Coming Up',
  day_later_today: 'Later today',
  day_nobody_later: 'Nobody else is due today.',
  day_off_tomorrow: 'Weekly Off Tomorrow · {date}',
  day_off_not_published: 'Not published yet.',
  day_nobody_off: 'Nobody is off.',
  day_next_holiday: 'Next holiday: {name}, {date}',
  early_outs_review: 'Review',
  today_nobody: 'Nobody',
  today_empty_in: 'Nobody is punched in right now.',
  today_empty_out: 'Nobody has punched out yet.',
  today_empty_none: 'Everyone has punched in.',
  today_empty_off: 'Nobody is off.',
  att_empty_present: 'Nobody was present.',
  att_empty_absent: 'Nobody was absent.',
  today_hours_empty: 'No hours recorded yet.',
  att_download_week_help:
    'Report for the selected week: hours per employee and day, totals, target and shortfall.',
  today_since: 'since {time}',
  today_below_target: 'Below Target',
  today_off_day: 'Weekly Off',
  today_holiday: 'Holiday',
  today_is_holiday: 'Company holiday: {name}',
  today_refresh: 'Refreshes every 30 s · server time {time}',
  feed_title: 'Notifications',
  feed_title_unread: 'Notifications ({n} unread)',
  feed_mark_read: 'Mark All Read',
  feed_empty: 'Nothing needs you. Requests, failed jobs and flagged punches show up here.',
  /** Pinned rows are a standing state, not a moment: "3 h ago" would be a lie about them. */
  feed_now: 'Now',
  flags_title: 'Open Flags',
  flags_sub:
    'Punches the system thought were worth a look. Resolving one records that you have seen it — it never changes a punch, an hour or a day.',
  flags_see_day: 'See The Day',
  flags_resolve_all: 'Resolve All ({n})',
  flags_none: 'Nothing needs attention.',
  // The dashboard's one line about flags, and the way to the page.
  flags_summary: '{n} punch(es) the system thought were worth a look.',
  flags_review: 'Review',
  /** Flags with no worker attached — a sign-in with a cancelled passkey, say. */
  flags_system: 'System',
  flags_resolve: 'Resolve',
  flags_resolve_n: 'Resolve {n}',
  flags_show_all: 'Show All {n}',
  flags_hide: 'Hide',
  flags_resolve_confirm: 'Mark Resolved',
  flags_resolve_bar: 'Resolve flags',
  flags_selected: '{n} selected',
  flags_pick_all: 'Select every flag shown',
  flags_pick: 'Select the flag for {name}',
  flags_col_flag: 'Flag',
  flags_col_when: 'When',
  flags_col_detail: 'Detail',
  flags_note_placeholder: 'Note (Optional)',

  // worker detail
  wd_late_in: 'Late In by {n}',
  wd_early_out: 'Early Out by {n}',
  wd_office_hours_note:
    'Office hours for this employee are {start}–{end}. These never block a punch or change the hours credited.',
  wd_off_this_week: 'Weekly Off This Week',
  wd_details: 'Details',
  wd_no_punchout_month: 'No Punch-Out This Month',
  // Days, not hours: a month's shortfall is not a figure anybody here works to.
  wd_col_type: 'Type',
  wd_col_time: 'Server Time',
  wd_col_source: 'Source',
  wd_col_distance: 'Distance',
  wd_col_accuracy: 'Accuracy',
  wd_col_ip: 'IP',
  wd_office_ip: 'Office IP',
  wd_correction: 'Correction',
  wd_superseded: 'Superseded',
  wd_rejected: 'Rejected Attempts',
  wd_client_reported: 'Tapped from Outside',
  wd_corrections_placeholder:
    'Corrections (set the real out-time with a reason) arrive in Phase 2. The original punch is always kept.',

  // settings
  settings_office: 'Office Location and Geofence',
  settings_pin_help:
    'Click the map (or drag the pin) to set the office position. Punches are accepted only inside the circle.',
  settings_pin_required: 'Set the office position on the map first.',
  settings_office_name: 'Office Name',
  settings_lat: 'Latitude',
  settings_lng: 'Longitude',
  settings_radius: 'Geofence Radius (m)',
  settings_office_ip: 'Office Public IP',
  settings_detect_ip: 'Detect from My Current Connection',
  settings_your_ip: 'you are on {ip}',
  time_hours: 'Hours',
  time_minutes: 'Minutes',
  time_meridiem: 'AM or PM',
  time_am: 'AM',
  time_pm: 'PM',
  settings_install_route: 'How Employees Install The App',
  settings_install_route_help: '(what the Get The App page tells employees to do)',
  settings_route_download: 'Not set up yet — employees are told to ask the office',
  settings_route_play_group: 'Play Store — testing group (link below)',
  settings_route_play_public: 'Play Store — public listing',
  settings_app_required: 'Android employees must punch from the app',
  settings_app_required_all: 'Require it of everyone, not only those who have used it',
  settings_app_required_help:
    'A punch from a browser cannot be checked for fake GPS or a rooted phone. Mark iPhone employees on their own page — there is no iPhone app.',
  wd_app: 'App',
  wd_app_exempt: 'Excused — cannot use the app',
  wd_app_exempt_do: 'Cannot use the app (iPhone)',
  wd_app_unexempt_do: 'Remove the exemption',
  wd_app_exempt_why:
    'Why can this employee not use the app? (for example: uses an iPhone). This is recorded.',
  wd_app_unexempt_why: 'Why is the exemption being removed? This is recorded.',
  punch_needs_app: 'Punching needs the SIVA Attendance app on this phone.',
  punch_needs_app_link: 'How to install it',
  settings_play_group: 'Play Testing Group Link',
  settings_play_group_help: '(the Google Group an employee joins to install from Play)',
  settings_office_ip_help: '(optional; marks punches from the office Wi-Fi)',
  settings_times: 'Times (Office Clock)',
  settings_cutoff: 'Day Cutoff',
  settings_cutoff_help: '(open shifts are auto-closed here; the working day starts here)',
  settings_digest: 'Admin Digest',
  settings_digest_help: '(who is still punched in)',
  settings_daily_report: 'Daily Report Email',
  settings_daily_report_help: '(PDF of the day’s attendance to every admin)',
  settings_monthly_report: 'Send the monthly report on the 1st to all admins',
  settings_reminder: 'Employee Punch-Out Reminder',
  settings_reminder_help: '(push reminder, phase 2)',
  settings_shift_times: 'Shift Timings',
  settings_shift_times_help:
    'When each shift runs, for the roster employees read. Hours are unchanged by this: nine for full time, five for part time, whenever they are worked. A shift may end after midnight — set the day cutoff above to a time after the latest end.',
  settings_caps: 'Weekly-Off Caps per Day, per Shift',
  settings_caps_help:
    'Maximum employees off on any one day, counted within each shift: with a cap of 3, three people can be off on the 1st shift and three on the 2nd. Applies to newly generated plans and to moves.',
  settings_cap_total: 'Total',
  settings_cap_ft: 'Full-Time',
  settings_cap_pt: 'Part-Time',
  settings_saved: 'Saved. Changed: {fields}.',
  settings_unchanged: 'Nothing changed.',
  settings_last_saved: 'Last saved {date}',
  settings_unsaved: 'Unsaved Changes',
  // session
  session_expired: 'Your session ended. Please sign in again.',

  // install prompt
  // Add to home screen: a step of its own after passkey setup, and reopenable from Profile.
  a2hs_title: 'Add SIVA To Your Home Screen',
  a2hs_why:
    'Punching in becomes one tap from your home screen, instead of finding a browser tab every morning.',
  a2hs_skip: 'Skip for Now',
  a2hs_continue: 'Continue',
  a2hs_install: 'Install',
  a2hs_installed: 'Added. You can open SIVA from your home screen now.',
  a2hs_ios_1: 'Tap the Share button at the bottom of Safari',
  a2hs_ios_2: 'Scroll down and tap "Add to Home Screen"',
  a2hs_ios_3: 'Tap Add. SIVA appears with your other apps.',
  a2hs_ios_art_alt: 'An iPhone with the Share button highlighted in the Safari toolbar',
  a2hs_android_or: 'Or add it yourself:',
  a2hs_android_no_button: 'Add it from the browser menu:',
  a2hs_android_1: 'Tap the menu button in Chrome',
  a2hs_android_2: 'Tap "Add to Home screen", then Add.',
  a2hs_other_1: 'Open your browser menu.',
  a2hs_other_2: 'Choose "Add to Home screen" or "Install", then confirm.',
  a2hs_reopen: 'Add to Home Screen',
  a2hs_reopen_help: 'Show the steps for putting SIVA on your home screen again.',

  // my hours
  hours_this_week: 'This Week',
  hours_this_month: 'This Month',
  hours_view_week: 'Week',
  hours_view_month: 'Month',
  hours_week_of: 'Week of {date}',
  hours_range: 'Which days to show',
  hours_earlier: 'Earlier',
  hours_later: 'Later',
  hours_no_days: 'No working days recorded in this range.',
  hours_days_present: 'Present',
  hours_days_absent: 'Absent',
  hours_days_off: 'Weekly Off',
  hours_worked_week: 'Worked this week',
  hours_worked_month: 'Worked this month',
  hours_of_target: 'of {target} target so far',
  hours_off_taken: 'Taken',
  hours_off_remaining: 'Remaining',
  hours_off_split: '{taken} taken · {remaining} remaining',
  hours_days_no_out: 'No Punch\u2011Out',
  hours_cal_off: 'Weekly Off',
  hours_cal_holiday: 'Holiday',
  hours_cal_absent: 'Absent',
  hours_tap_a_day: 'Tap a day to see its in, out and hours.',
  // The letter a day chip carries, as the owner set them: P, A, WO, H.
  hours_chip_present: 'P',
  hours_chip_absent: 'A',
  hours_chip_off: 'WO',
  hours_chip_holiday: 'H',
  hours_chip_today_word: 'Today',
  hours_title: 'My Hours',
  hours_col_day: 'Day',
  hours_col_status: 'Status',
  hours_col_in: 'In',
  hours_col_out: 'Out',
  hours_col_hours: 'Hours',
  hours_col_diff: '± Target',
  hours_st_present: 'Present',
  hours_st_no_punchout: 'No Punch-Out',
  hours_st_worked_on_off: 'Worked on Off Day',
  hours_st_absent: 'Absent',
  // "No Punch Out" at the owner's request: the day is open and nothing has been decided about it.
  hours_st_not_yet: 'No Punch Out',
  hours_st_off: 'Weekly Off',
  hours_st_holiday: 'Holiday',
  hours_no_punchout_mark: 'Auto-Closed',
  hours_running: 'Running',
  hours_total_worked: 'Worked {worked} of {target}',
  hours_total_short: 'Shortfall {n}',
  hours_total_on_target: 'On Target',
  hours_total_over: 'overtime {n}',
  // The one number a worker wants: how far ahead or behind they are, with the two parts under it.
  hours_total_net_behind: 'Behind by {n}',
  hours_total_net_ahead: 'Ahead by {n}',
  hours_total_net_parts: 'Shortfall {short} · Overtime {over}',
  hours_total_days:
    'Present {present} · Absent {absent} · Weekly Off {off} · No Punch-Out {no_punchout}',
  hours_note:
    'Forgot to punch out? The day is closed automatically overnight and counts only the hours on the clock, up to a normal day. Ask the office to correct the real out-time.',
  punch_weak_gps_short: 'Weak GPS',

  // profile / push
  push_title: 'Notifications on This Phone',
  push_intro:
    "Turn on to get a reminder when you forget to punch out and a notice when next week's off days are published.",
  push_on: 'On for This Phone',
  push_off: 'Off',
  push_unavailable: 'Notifications are not set up on the server yet.',
  push_ios_install:
    'On iPhone, notifications work only after the app is added to your home screen.',
  push_ios_how: 'How to Add It',
  profile_sign_out_help:
    'Signing out only removes this session. Your fingerprint / Face ID sign-in keeps working.',
  // shell
  header_account: 'Your Account',
  home_this_week: 'This Week',
  home_see_all: 'See All',
  home_today: 'Today',
  home_off: 'Off',
  home_off_not_published: 'Week off not published yet.',
  home_attendance_log: 'Attendance Log',
  home_view_all: 'View All',
  home_log_date: 'Date',
  home_log_in: 'In',
  home_log_out: 'Out',
  home_log_total: 'Total',
  home_log_status: 'Status',
  punch_trio_in: 'Punched In',
  punch_trio_out: 'Punched Out',
  punch_trio_left: 'Left Today',
  punch_trio_done: 'Done',
  greet_morning: 'Good Morning',
  greet_afternoon: 'Good Afternoon',
  greet_evening: 'Good Evening',
  theme_light: 'Switch to Light Theme',
  theme_dark: 'Switch to Dark Theme',
  search_workers: 'Search Employees',
  nav_reports: 'Reports',
  reports_sub: 'Attendance over any range, for everyone or one employee.',
  reports_day: 'Day',
  reports_week: 'Week',
  reports_month: 'Month',
  reports_custom: 'Custom',
  reports_week_of: 'Week Of',
  reports_month_label: 'Month',
  reports_from: 'From',
  reports_to: 'To',
  reports_worker: 'Employee',
  reports_all_workers: 'All Employees',
  reports_generate: 'Generate',
  reports_pdf: 'PDF',
  reports_xlsx: 'Excel',
  reports_workers: 'Employees',
  reports_days: 'Days',
  reports_present_days: 'Present Days',
  reports_late_in: 'Late In',
  reports_left_early: 'Left Early',
  reports_early_out: 'Early Out',
  reports_hours: 'Hours',
  reports_target: 'Target',
  reports_shortfall: 'Shortfall',
  reports_by_worker: 'By Employee',
  reports_date: 'Date',
  reports_in: 'In',
  reports_out: 'Out',
  reports_start: 'Pick a range and an employee, then press Generate.',
  reports_empty: 'Nothing recorded in this range.',
  today_hours_chart: 'Hours Today',
  today_hours_chart_sub: 'Per employee, against their daily target',
  today_target_line: 'Target',
  today_week_chart: 'Man Power This Week',
  today_week_chart_sub_dates: 'Monday {from} to Sunday {to}. Today counts as it happens.',
  today_week_chart_sub: 'The number on each bar is how many were present that day',
  today_week_empty: 'No completed days yet.',
  workers_col_hours: 'This Month',
  punch_running_label: 'today',
  hours_tile_shortfall: 'Shortfall',

  // ---- bank details (a worker's own, and the admin's view of one worker's) ----
  bank_title: 'Bank Details',
  bank_intro:
    'Where you are paid, and your PAN. You can add this later from your profile — attendance is unaffected — but no pay slip is issued until all four are on record.',
  bank_intro_edit: 'Where you are paid. This appears only on your own pay slip.',
  bank_name: 'Bank Name',
  bank_name_hint: 'Name of the bank',
  bank_choose: 'Choose the bank',
  bank_other: 'Other (type the name)',
  bank_other_name: 'Which Bank',
  bank_account: 'Account Number',
  bank_account_hint: '00000000000',
  bank_account_help: 'Exactly as it appears in your passbook.',
  bank_save: 'Save',
  bank_saving: 'Saving…',
  bank_later: 'Add Later',
  bank_saved: 'Saved.',
  bank_privacy: 'Only you and the office can see this. It is never shown to other employees.',
  bank_missing: 'Not provided',
  bank_edit: 'Add or change bank details',
  bank_admin_sub: 'Used only on the pay slip for this employee. Every change is audited.',
  bank_admin_none:
    'Not provided. Attendance is unaffected; no pay slip is issued until the bank name, account number, IFSC and PAN are all on record.',
  bank_admin_missing: 'Bank Details Missing',
  bank_admin_saved: 'Bank details saved.',
  bank_ifsc: 'IFSC Code',
  bank_ifsc_hint: 'SBIN0001234',
  bank_ifsc_help: 'Eleven characters, on your passbook or cheque book.',
  bank_pan: 'PAN',
  bank_pan_hint: 'ABCDE1234F',
  bank_pan_help: 'As printed on your PAN card.',
  bank_missing_fields: 'Missing: {fields}',
  bank_incomplete: 'No pay slip is issued until these are all on record.',
  pay_needs_bank: 'No slip until the bank details are complete. Missing: {fields}.',
  punch_bank_reminder: 'Your pay slip needs your {fields}.',
  punch_bank_add: 'Add now',

  // ---- pay slips: the worker's own, and the admin's month ----
  nav_documents: 'Pay Slips',
  docs_title: 'Your Pay Slips',
  docs_sub: 'What you were paid each month, and the slip for it.',
  docs_none: 'Nothing yet. A pay slip appears here after the office publishes it.',
  docs_superseded: 'replaced by a later version',
  docs_gross: 'Basic Salary',
  docs_deduction: 'Leave Deduction ({n})',
  docs_tds: 'Tax deducted at source (TDS), {rate}% u/s {section}',
  docs_net: 'Net paid',
  docs_words: 'In words',
  docs_bank: 'Paid to',
  docs_bank_missing: 'Bank details were not on record',
  docs_pan: 'PAN',
  docs_issued: 'Issued',
  docs_show: 'See The Figures',
  docs_hide: 'Hide',
  docs_download: 'Download PDF',
  docs_pick_month: 'Month',
  docs_saved_to: 'Saved to {where}. Open it from the notification or your Files app.',
  docs_no_agreement: 'No agreement has been issued to you yet.',

  nav_payments: 'Pay Slips',
  pay_title: 'Pay Slips',
  pay_sub: 'Figures for {month}. Publishing issues the pay slip to the employee.',
  pay_sub_loading: 'Figures for the month.',
  pay_month: 'Month',
  pay_worker: 'Employee',
  pay_days: 'Days Employed',
  pay_not_served: '{n} not attended',
  pay_gross: 'Basic Salary',
  pay_deduction: 'Deduction',
  pay_tds: 'TDS',
  pay_net: 'Net Payable',
  pay_status: 'Slip',
  pay_overtime: 'Overtime (₹)',
  pay_no_correction: 'The published slip already says what the figures say.',
  pay_actions_for: 'Actions for {name}',
  saving: 'Saving…',
  saved: 'Saved',
  pay_overtime_for: 'Overtime for {name}, in rupees',
  pay_overtime_bad: 'Whole rupees, 0 or more',
  docs_overtime: 'Overtime',
  pay_prev_month: 'Previous month',
  pay_next_month: 'Next month',
  pay_publish: 'Publish',
  pay_republish: 'Publish Correction',
  pay_publishing: 'Publishing…',
  pay_publish_all: 'Publish All ({n})',
  pay_published: 'Published {number}.',
  pay_published_replacing: 'Published {number}, replacing {old}.',
  pay_published_all: 'Published {n} pay slips.',
  pay_published_some: 'Published {n} before this stopped it:',
  pay_published_badge: 'Published (v{v})',
  pay_not_published: 'Not published',
  pay_differs: 'The figures have changed since this was published.',
  pay_pdf: 'PDF',
  pay_xlsx: 'Excel',
  pay_nobody: 'Nobody was engaged in this month.',
  pay_month_running:
    'This month is not over. The figures move as days are worked, and a pay slip cannot be published until it ends.',
  pay_no_fees_at_all:
    'No salary is on record for anybody. Set one on an employee’s page — open the employee, then Salary — and their pay slip can be published from here.',
  pay_no_fee_short: 'No salary set',
  // ---- employment agreement ----
  agr_title: 'Employment Agreement',
  agr_sub:
    'The contract between the company and this employee. Issuing it states the salary and the start date; re-issuing makes a new version and keeps the old one.',
  agr_effective: 'Takes Effect',
  agr_fee: 'Salary',
  agr_per_month: 'per month',
  agr_tds: 'TDS',
  agr_articles: 'Articles',
  agr_issue: 'Issue',
  agr_reissue: 'Issue A New Version',
  agr_issuing: 'Issuing…',
  agr_issued: 'Issued {number}.',
  agr_issued_replacing: 'Issued {number}, replacing {old}.',
  agr_issued_on: 'Issued {date}',
  agr_none: 'No agreement has been issued to this employee yet.',
  agr_current: 'Current',
  agr_superseded: 'Replaced',
  // Replaced versions are kept and downloadable — somebody may be holding a printed copy — but
  // they are not what the card is for, so they sit behind this.
  agr_show_old: 'Show {n} replaced',
  agr_hide_old: 'Hide replaced',
  agr_pdf: 'PDF',
  // Named the "independent-contractor clause" until 16 September 2026, which stopped being the
  // name of anything when Article 1 was rewritten to say the person is employed. The two clauses
  // a lawyer should actually look at are the one that says what the relationship is and the one
  // that restricts who else the person may work for.
  agr_not_reviewed:
    'This template was drafted for this company and has not been reviewed by a lawyer. Have one read it before you rely on it — particularly the appointment clause and the exclusivity clause.',
  docs_agreement: 'Employment Agreement',
  docs_agreement_sub: 'Your contract with the company.',

  // ---- the Android app: which build is current, which is the floor, who is on what ----
  // ---- the download page at /android ----
  dl_title: 'SIVA Attendance For Android',
  dl_sub:
    'Install this on your phone to punch in and out. It comes from SIVA, not from the Play Store.',
  dl_version: 'Version',
  dl_released: 'Released',
  dl_download: 'Download {version}',
  dl_none_yet: 'No version has been published yet. Ask the office.',
  dl_unavailable:
    'We could not reach the server to check the current version. Try again in a moment.',
  dl_steps_title: 'Installing It',
  dl_steps_intro:
    'Android asks for permission before it installs anything that did not come from the Play Store. This is expected, and you only do it once.',
  dl_step_1: 'Tap Download above. The file saves to your phone.',
  dl_step_2:
    'Open the downloaded file — tap the notification, or find it in Files under Downloads.',
  dl_step_3:
    'Android will say it cannot install apps from this source. Tap Settings on that message, then turn on "Allow from this source", and go back.',
  dl_step_4: 'Tap Install, then Open.',
  dl_step_5: 'Sign in with the fingerprint you already set up. Nothing else changes.',
  dl_oem_note:
    'The exact words differ by phone: Samsung says "Install unknown apps", Xiaomi asks twice and may mention "MIUI optimisation". If you cannot find it, call the office and somebody will walk you through it.',
  dl_diagram_alt: 'A phone dialog with a highlighted Allow button',
  dl_diagram_allow: 'Allow',
  dl_protect_title: 'The Play Protect Warning',
  dl_protect_body:
    'Your phone may say "Unsafe app blocked" or offer to scan the app. That is Play Protect noticing an app it has not seen before, not a virus. Choose "Install anyway" or "More details → Install anyway".',
  dl_protect_why:
    'It says this about every app that does not come from the Play Store, including this one. The file check above is how you know it is ours.',
  dl_help: 'Stuck? Call the office:',
  login_get_the_app: 'On Android? Install the SIVA app →',

  // ---- getting the app, and the public privacy policy ----
  app_get_title: 'Get The App',
  app_get_why:
    'Punching needs the app. It takes a minute to set up and you only do it once on this phone.',
  app_get_optin_help:
    'Tap Become a tester on the page that opens, then Download it on Google Play. If it says the app is not available, finish step 1 first and wait a few minutes.',
  app_get_step_group: 'Join the employee group',
  app_get_step_group_help:
    'Sign in with the Google account on this phone, then tap Join group at the top of the page. If it says you do not have permission to see the content, ignore that — it is about reading messages, not about joining. The Play Store will not give you the app until you have joined.',
  app_get_step_optin: 'Become a tester',
  app_get_step_optin_help:
    'Tap Become a tester on the page that opens. This is what lets the Play Store give you the app.',
  app_get_step_install: 'Install the app',
  app_get_install_locked:
    'Join the group first — the Play Store will not offer the app until you have.',
  app_get_already_joined: 'I have already joined the group',
  app_get_step_install_help:
    'The Play Store page opens. Tap Install, then come back here and sign in with your fingerprint. Updates arrive on their own after this.',
  app_get_wait:
    'After you join, Google usually takes 5 to 10 minutes before it will give you the app — sometimes longer. Until then step 2 says the app is not available, or the download fails. That is normal and nothing is wrong: wait a few minutes and tap step 2 again. Do not search for the app in the Play Store, it will not be there.',
  app_get_install: 'Install From Play Store',
  app_get_public_help:
    'Tap Install, then come back here and sign in with your fingerprint. Updates arrive on their own after this.',
  app_get_join_group: 'Join The Group',
  app_get_become_tester: 'Become A Tester',
  app_get_open_play: 'Open Play Store',
  app_get_not_ready:
    'The app is not ready to install yet. Ask the office — they will tell you when it is.',
  app_get_done: 'Done, Take Me In',
  profile_get_app_sub: 'Moving to a new phone, or reinstalling? The steps are here.',
  profile_get_app_link: 'Get the app on this phone',
  privacy_title: 'Privacy Policy',
  privacy_updated: 'Last updated {date}',

  // ---- the app telling a worker it is out of date ----
  update_via_play: 'It will update through the Play Store.',
  update_open_play: 'Open Play Store',
  too_old_title: 'Update The App To Punch',
  too_old_body:
    'This version of the app is too old to record a punch. Update it and you can clock in as usual — it takes a minute.',
  too_old_builds: 'You have build {mine}. Build {needed} or newer is needed.',
  too_old_stale:
    'We could not check for the update just now — you may have no signal. Try again when you do.',
  too_old_recheck: 'Check Again',
  too_old_checking: 'Checking…',
  too_old_no_link:
    'The update could not be fetched. Please call the office and somebody will help you install it.',
  too_old_call: 'Stuck? Call the office:',
  too_old_rest_works:
    'Your hours, weekly off and pay slips still work as normal while you sort this out.',

  android_title: 'Android App',
  android_sub: 'Which build each phone is on, and whether an old one may still punch.',
  android_latest: 'Latest Build Uploaded Here',
  android_minimum: 'Minimum To Punch',
  android_build: 'build {n}',
  android_published: 'Published {date}',
  android_no_release: 'No build has been published yet.',
  android_no_minimum: 'No minimum — any build can punch',
  android_set_minimum: 'Set The Minimum To',
  android_min_apply: 'Apply',
  android_min_would_stop: 'This would stop {n} {people} punching until they update: {names}.',
  android_min_confirm_title: 'Raise The Minimum App Version?',
  android_min_confirm_stops:
    '{n} {people} on an older build and will not be able to punch until they update: {names}. They are told what to do and given the download, and you can lower this again at any time.',
  android_min_confirm_none:
    'Nobody is currently on a build below this, so no one will be stopped from punching.',
  android_min_set: 'Minimum updated. Nobody was below it.',
  android_min_set_stops: 'Minimum updated. {n} {people} must update before they can punch.',
  android_who: 'Who Is On Which Build',
  android_version: 'App Version',
  android_last_punch: 'Last Punched',
  android_not_the_app: 'Not using the app',
  android_below: 'Below the minimum',
  android_behind_count: '{n} {people} cannot punch until they update.',
  android_nobody_on_app: 'No punches have come from the app yet.',
  android_add_release: 'Upload A New Build',
  android_file: 'APK File',
  android_upload_intro:
    'Pick the APK the build produced and paste the SHA-256 it printed. The server hashes the file it receives and refuses it if the two do not match, so a stale file cannot be published by accident.',
  android_uploading: 'Uploading…',
  android_uploaded: 'Recorded {version} ({mb} MB). Employees update from Google Play.',
  android_save_release: 'Save Build',
  android_release_saved: 'Build recorded. The app will offer it on next launch.',
  android_version_name: 'Version Name',
  android_version_code: 'Build Number',
  android_sha: 'SHA-256 Of The APK',
  android_sha_hint: 'The 64-character hash the build printed',
  android_notes: 'Release Notes (Shown In The App)',
  android_is_latest: 'Latest',
  android_is_minimum: 'Minimum',
  android_withdraw: 'Withdraw',
  android_withdraw_title: 'Withdraw {version}?',
  android_withdraw_body:
    'The app will stop offering this build. Anyone who already installed it keeps it. Use this when a release turns out to be bad.',
  android_withdrawn: '{version} withdrawn.',

  // Withdrawing a published document: it was issued and should not be relied on. Used when a
  // correction is impossible because the figures it should have carried do not exist.
  pay_withdraw: 'Withdraw',
  pay_withdraw_prompt:
    'Withdraw {number}? It stops appearing on the employee page and keeps its row for the record. Say why:',
  pay_withdraw_reason_short: 'Give a reason of at least three characters.',
  pay_withdrawn:
    'Withdrawn {number}. The employee no longer sees it, and the month can be issued again.',
  pay_tds_note_title: 'About The Tax Line',
  pay_tds_note:
    'TDS is deducted at each employee’s own rate, 1% under section 194C unless you change it on their page. This system deducts and records it — it does not deposit it, file a return, or issue Form 16A. Those remain yours to do.',
  pay_tds_note_2:
    'Section 194C has thresholds below which no deduction is required at all. This system does not track them: it deducts at the rate you set, on every pay slip. If an employee is below the threshold, set their rate to 0 on their page.',
} as const;

export type MessageKey = keyof typeof en;
